import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { FooterButtonType, MessageSenderType } from '../../../interfaces';
import { faKeyboard, faPlus, faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { FooterAddonComponent } from '../../molecules';
import {
  MessageKind,
  Participant,
  ParticipantKind,
  MessageTemplateLight,
  ConversationTypeTemplateKind,
} from '@clientwire/api';
import { ButtonComponent, FilesToUpload, InputComponent, MessageComponent } from '@akostm/orbit-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Overlay } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { TakePhotoOverlayComponent } from '../../templates';
import { logger, SharedUtils } from '../../../utils';
import { ActiveConversationStore } from '../../../stores';
import { ConversationHelperService } from '../../../services';
import { faPaperPlaneTop, faUnlock } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'shared-conversation-footer',
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    InputComponent,
    FooterAddonComponent,
    MessageComponent,
    TranslatePipe,
  ],
  templateUrl: './conversation-footer.component.html',
  styleUrl: './conversation-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ConversationFooterComponent {
  readonly sendIcon = faPaperPlaneTop;
  readonly addonButtonIcon = signal(faPlus);
  readonly unlockIcon = faUnlock;
  readonly joinIcon = faRightToBracket;
  readonly addonBarVisible = signal(false);
  readonly deletedAttachment = signal<MessageKind | null>(null);
  readonly #defaultController = new FormControl<string | null>(null);
  readonly messageSenderTypeEnum = MessageSenderType;
  readonly messageInput = viewChild(InputComponent);
  readonly maxMessageLength = 4096;
  readonly messageInputAriaLabel = computed(() => {
    const label = this.#translateService.instant('SHARED.CONVERSATION_FOOTER.INPUT_LABEL');
    const helper = this.#translateService.instant('SHARED.CONVERSATION_FOOTER.HELPER_TEXT', {
      maxChars: this.maxMessageLength,
      attachmentCount: this.files()?.length ?? 0,
    });
    return `${label}. ${helper}`;
  });

  //#region Inputs
  readonly senderType = input.required<MessageSenderType>();
  readonly conversationId = input.required<string>();
  readonly templates = input<Map<string, MessageTemplateLight> | undefined>();
  readonly isConversationLocked = input(false);
  readonly isConversationArchived = input(false);
  readonly conversationLockNote = input<string>('SHARED.CONVERSATION_LOCKED.DEFAULT');
  readonly nextMessageSourceId = input<string>();
  readonly messageInputController = input<FormControl>(this.#defaultController);
  readonly loading = input<boolean>(false);
  readonly userId = input<string | null>();
  readonly userIsNotParticipant = input<boolean>();
  readonly noUsers = input<boolean>();
  readonly conversationParticipants = input<Participant[]>();
  //endregion

  //#region Outputs
  readonly unlockAction = output<string>();
  readonly sendMessage = output<boolean>();
  readonly isTyping = output<boolean>();
  readonly joinConversation = output<void>();
  //endregion

  //#region Models
  readonly files = model<FilesToUpload[]>();
  //#endregion

  //#region Services
  readonly #overlay = inject(Overlay);
  readonly #dialog = inject(Dialog);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #translateService = inject(TranslateService);
  //#endregion

  toggleFooterAddon() {
    this.addonBarVisible.update((value) => !value);
    this.addonButtonIcon.set(this.addonBarVisible() ? faKeyboard : faPlus);
  }

  hideFooterAddon() {
    this.addonBarVisible.set(false);
    this.addonButtonIcon.set(faPlus);
  }

  handleAddonClick(type: FooterButtonType) {
    this.hideFooterAddon();

    switch (type) {
      case FooterButtonType.WebCamera: {
        this.#capturePhoto();
        break;
      }
      case FooterButtonType.Location: {
        this.#getLocation();
        break;
      }

      case FooterButtonType.MsgLocation: {
        const template = this.templates()?.get(ConversationTypeTemplateKind.RequestLocation);
        const language = this.#determineLanguageForRequestLocation();
        const text = template?.texts[language] ?? template?.texts['en'] ?? '';

        this.#conversationHelperService.createNewMessage(
          this.conversationId(),
          'REQUEST_LOCATION',
          this.nextMessageSourceId(),
          text
        );
        break;
      }

      default: {
        logger.debug(type);
        break;
      }
    }
  }

  sendMessageHandler(event: boolean) {
    this.hideFooterAddon();
    this.sendMessage.emit(event);
  }

  addonButtonStyle() {
    if (this.addonButtonIcon() === faPlus) {
      return 'addon-button-plus';
    }
    return 'addon-button-keyboard';
  }

  handleDeleteAttachment(fileKind: MessageKind) {
    this.deletedAttachment.set(fileKind);
  }

  #capturePhoto() {
    const positionStrategy = this.#overlay.position().global().centerHorizontally().bottom();
    const dialogRef = this.#dialog.open(TakePhotoOverlayComponent, {
      width: '61.25rem',
      panelClass: 'location-dialog',
      data: {
        location,
      },
      positionStrategy,
    });

    dialogRef.componentInstance?.sendPhoto.subscribe(async (value) => {
      if (value) {
        const file = SharedUtils.dataURIToBlob(value);
        if (!file) throw new Error('No file found');
        const dateNow = new Date().getTime();
        this.#activeConversationStore.updateConversation(this.conversationId(), {
          files: [{ file: new File([file], `${dateNow}.png`, { type: 'image/png' }), src: value }],
        });
      }
      dialogRef.close();
    });
  }

  #getLocation() {
    this.#openLocationSelectorOverlay();
  }

  #openLocationSelectorOverlay(): void {
    this.#conversationHelperService.openLocationSelectorOverlay(
      this.conversationId(),
      this.nextMessageSourceId()
    );
  }

  onUnlockKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.unlockAction.emit(this.conversationId());
    }
  }

  onJoinConversationKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.joinConversation.emit();
    }
  }

  onSendMessageKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.sendMessageHandler(true);
    }
  }

  onAddonButtonKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleFooterAddon();
    }
  }

  #determineLanguageForRequestLocation(): string {
    const participants = this.conversationParticipants();
    if (!participants) {
      return this.#translateService.getCurrentLang();
    }

    // Get all client participants
    const clientParticipants = participants.filter((p) => p.kind === ParticipantKind.Client);

    if (clientParticipants.length === 0) {
      // No client participants, use current language
      return this.#translateService.getCurrentLang();
    }

    if (clientParticipants.length === 1) {
      // Single client participant, use their preferred language
      const preferredLanguage = clientParticipants[0]?.preferredLanguage;
      return preferredLanguage ?? this.#translateService.getCurrentLang();
    }

    // Multiple client participants - check if they all have the same preferred language
    const languages = clientParticipants
      .map((p) => p.preferredLanguage)
      .filter((lang): lang is string => lang !== null && lang !== undefined);

    const uniqueLanguages = [...new Set(languages)];

    if (uniqueLanguages.length === 1 && uniqueLanguages[0]) {
      // All client participants have the same preferred language
      return uniqueLanguages[0];
    }

    // Multiple different languages or some without preference - use current language
    return this.#translateService.getCurrentLang();
  }
}
